"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import atlanta from "@/app/(public)/shared/assets/venues/atlanta.jpg";
import austin from "@/app/(public)/shared/assets/venues/austin.jpg";
import barcelona from "@/app/(public)/shared/assets/venues/barcelona.jpg";
import barcelona2 from "@/app/(public)/shared/assets/venues/barcelona_2.jpg";
import cancun from "@/app/(public)/shared/assets/venues/cancun.jpg";
import cancun2 from "@/app/(public)/shared/assets/venues/cancun2.webp";
import costarica from "@/app/(public)/shared/assets/venues/costa_rica.jpg";
import dallas from "@/app/(public)/shared/assets/venues/dallas.webp";
import fourseasons from "@/app/(public)/shared/assets/venues/fourseasons-hawaii.jpg";
import hotelcasadelmar from "@/app/(public)/shared/assets/venues/hotelcasadelmar.jpg";
import lisbon from "@/app/(public)/shared/assets/venues/libson.jpg";
import maui from "@/app/(public)/shared/assets/venues/maui.webp";
import mexicocity from "@/app/(public)/shared/assets/venues/mexicocity.webp";
import miami from "@/app/(public)/shared/assets/venues/miami.jpg";
import napa from "@/app/(public)/shared/assets/venues/napa.webp";
import nashville from "@/app/(public)/shared/assets/venues/nashville.jpg";
import neworleans from "@/app/(public)/shared/assets/venues/neworleans.jpg";
import newyork from "@/app/(public)/shared/assets/venues/newyork.jpg";
import rimba from "@/app/(public)/shared/assets/venues/rimba.webp";
import sandiego from "@/app/(public)/shared/assets/venues/sandiego.avif";
import sandomenicopalace from "@/app/(public)/shared/assets/venues/sandomenicopalace-italy.webp";
import stregisboraboraresort from "@/app/(public)/shared/assets/venues/stregisboraboraresort.webp";
import stregismaldivesvommuliresort from "@/app/(public)/shared/assets/venues/stregismaldivesvommuliresort.jpg";
import washington from "@/app/(public)/shared/assets/venues/washington.jpg";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import LoadingDog from "@/components/ui/loading-components/LoadingDog";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

// Play icon component
const PlayIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5v14l11-7z" />
  </svg>
);

// Type definition for city locations (for "Top Destinations" section)
interface CityLocation {
  city: string;
  country: string;
  type: string;
  image: string | any; // Can be StaticImageData (imported) or string (URL)
}

// Type definition for venue properties (for hero section cards)
interface Venue {
  city: string;
  venueType: string;
  venueName: string;
  image: string | any; // Can be StaticImageData (imported) or string (URL)
}

// Actual luxury venue images from world-famous vacation spots
const venueImages = [
  hotelcasadelmar, // Hotel Casa del Mar - Santa Monica
  stregisboraboraresort, // The St. Regis Bora Bora Resort
  rimba, // Rimba by Ayana Bali
  stregismaldivesvommuliresort, // The St. Regis Maldives Vommuli Resort
  sandomenicopalace, // San Domenico Palace, Taormina
  fourseasons, // Four Seasons Resort Maui at Wailea
];

// Venue data - actual luxury venues from world-famous vacation spots
const venues: Venue[] = [
  {
    city: "Santa Monica",
    venueType: "Luxury Beach Hotels",
    venueName: "Hotel Casa del Mar",
    image: venueImages[0],
  },
  {
    city: "Bora Bora",
    venueType: "Overwater Resorts",
    venueName: "The St. Regis Bora Bora Resort",
    image: venueImages[1],
  },
  {
    city: "Bali",
    venueType: "Tropical Resorts",
    venueName: "Rimba by Ayana Bali",
    image: venueImages[2],
  },
  {
    city: "Maldives",
    venueType: "Private Island Resorts",
    venueName: "The St. Regis Maldives Vommuli Resort",
    image: venueImages[3],
  },
  {
    city: "Taormina",
    venueType: "Historic Luxury Hotels",
    venueName: "San Domenico Palace, A Four Seasons Hotel",
    image: venueImages[4],
  },
  {
    city: "Maui",
    venueType: "Oceanfront Resorts",
    venueName: "Four Seasons Resort Maui at Wailea",
    image: venueImages[5],
  },
];

const venueBriefItems = [
  {
    label: "Event goal and format",
    description: "Purpose and format, such as a retreat or sales kickoff.",
  },
  {
    label: "Location",
    description: "Fixed or flexible city, nearby address, or destinations.",
  },
  {
    label: "Dates",
    description: "Range, nights, and flexibility.",
  },
  {
    label: "Headcount",
    description: "Expected total or range before registration closes.",
  },
  {
    label: "Venue type",
    description: "Hotel, resort, conference center, restaurant, or other.",
  },
  {
    label: "Budget",
    description: "Total and included costs: venue, rooms, food and beverage (F&B), audiovisual (AV), or full event.",
  },
  {
    label: "Meeting-space needs and timing",
    description:
      "List session, breakout, meal, outdoor, registration, stage, and exhibit spaces by day and time, plus overlaps, setup requirements, rehearsals, room flips, and load-in.",
  },
  {
    label: "Guest rooms",
    description: "Nightly room count, arrival/departure pattern, and VIP needs.",
  },
  {
    label: "Non-negotiables",
    description:
      "Accessibility, internet, transportation, catering, dietary, security, privacy, sustainability, and brand rules.",
  },
];

const sourcingSteps = [
  {
    title: "You submit the brief.",
    description:
      "The event flow captures your venue type, locations, attendee count, budget, dates, accommodations, meeting-space schedule, and other details. You can also name specific venues you want included.",
  },
  {
    title: "We handle venue outreach.",
    description:
      "Our AI prepares first-contact messages and follows up with venues for the proposal details your brief requires. For managed engagements, an event specialist helps with context that cannot be reduced to a form field.",
  },
  {
    title: "We organize the proposals.",
    description:
      "Venue replies and proposal documents are turned into structured comparisons, including the pricing categories and event details each venue provides.",
  },
  {
    title: "You choose how to move forward.",
    description:
      "Review the shortlist, resolve open questions, and select the venue that fits the whole brief. Support for negotiation, contracts, and payments depends on the plan selected for your event.",
  },
];

const proposalCriteria = [
  {
    term: "Total cost",
    description:
      "Rental, rooms, taxes, service charges, resort/destination fees, F&B minimums, AV, labor, internet, and outside-vendor fees.",
  },
  {
    term: "Capacity and layout",
    description:
      "Capacity in your layout, dimensions, sightlines, nearby breakouts, registration space, weather backup, and confirmed availability for each time slot.",
  },
  {
    term: "Guest rooms",
    description:
      "A room block holds guest rooms for attendees. Compare rooms per night, rate, cut-off date, upgrades, and distance to event spaces.",
  },
  {
    term: "Location and access",
    description:
      "Airport or rail access, transfer time, transit, parking, neighborhood safety, and site-wide accessibility.",
  },
  {
    term: "Audiovisual and internet",
    description:
      "Equipment, provider restrictions, labor, power, Wi-Fi capacity, acoustics, rigging, streaming, and recording.",
  },
  {
    term: "Food and beverage",
    description: "Minimums, menus, dietary support, service style, alcohol rules, kitchen access, and fees.",
  },
  {
    term: "Operations",
    description:
      "Load-in and load-out routes, freight access, storage, setup time, staffing, signage rules, and concurrent events.",
  },
  {
    term: "Concessions and policies",
    description:
      "Concessions are negotiated extras or discounts. Compare cancellation terms and attrition charges for unmet room or service commitments.",
  },
  {
    term: "Experience and fit",
    description: "Ambiance, privacy, service, nearby amenities, brand fit, and the planned attendee experience.",
  },
];

// Reusable card component for the image grid
const ImageCard = ({ venueIndex, delay = 0 }: { venueIndex: number; delay?: number }) => {
  const venue = venues[venueIndex % venues.length];
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleImageLoad = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsLoading(false);
  };

  const handleImageError = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsLoading(false);
    setHasError(true);
  };

  const handleImageLoadStart = () => {
    timeoutRef.current = setTimeout(() => {
      setIsLoading(true);
    }, 300); // Show loading after 300ms
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <motion.div
      className="w-full h-64 relative rounded-xl overflow-hidden"
      style={{
        border: "2px solid var(--landing-bg-primary)",
        outline: "3px solid #1c14231a", // stone-200 equivalent
      }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.8,
        delay: delay / 1000, // Convert ms to seconds
        ease: [0.16, 1, 0.3, 1], // Custom cubic-bezier
      }}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100">
          <LoadingDog size={48} />
        </div>
      )}

      {/* Error overlay */}
      {hasError && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100">
          <div className="text-center text-gray-500">
            <div className="text-sm">Failed to load image</div>
          </div>
        </div>
      )}

      {/* Background Image */}
      <img
        src={typeof venue.image === "string" ? venue.image : venue.image.src}
        alt="Event venue"
        className={`w-full h-full object-cover transition-opacity duration-200 ${
          isLoading ? "opacity-0" : "opacity-100"
        }`}
        onLoadStart={handleImageLoadStart}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {/* Dark Overlay for better text readability */}
      <div className="absolute inset-0 bg-linear-to-t from-black/50 via-black/20 to-transparent" />

      {/* City Pill - Top Left */}
      <div className="absolute top-3 left-3">
        <div
          className="text-tag-sm sm:text-tag-sm text-landing-primary px-3 py-1.5 rounded-full backdrop-blur-sm font-medium"
          style={{ backgroundColor: "rgba(255, 255, 255, 0.95)" }}
        >
          {venue.city}
        </div>
      </div>

      {/* Venue Info - Bottom Left */}
      <div className="absolute bottom-3 left-3 flex flex-col gap-1">
        <div className="text-tag-sm sm:text-tag-sm text-landing-white font-medium">{venue.venueType}</div>
        <div className="text-body-sm sm:text-tag-lg text-landing-white font-semibold">{venue.venueName}</div>
      </div>
    </motion.div>
  );
};

export default function VenueSourcing() {
  const { openModal, closeModal, isModalOpen } = useModalManager();
  const router = useRouter();
  // 12 locations for perfect grid layout (divisible by 1, 2, 3, and 4 columns)
  const topLocations: CityLocation[] = [
    { city: "Lisbon", country: "Portugal", type: "", image: lisbon },
    { city: "Barcelona", country: "Spain", type: "", image: barcelona },
    { city: "Miami", country: "Florida", type: "", image: miami },
    { city: "New York City", country: "New York", type: "", image: newyork },
    { city: "Austin", country: "Texas", type: "", image: austin },
    { city: "New Orleans", country: "Louisiana", type: "", image: neworleans },
    { city: "Nashville", country: "Tennessee", type: "", image: nashville },
    { city: "Mexico City", country: "Mexico", type: "", image: mexicocity },
    { city: "Cancun", country: "Mexico", type: "", image: cancun },
    { city: "Dallas", country: "Texas", type: "", image: dallas },
    { city: "Atlanta", country: "Georgia", type: "", image: atlanta },
    { city: "Washington", country: "D.C.", type: "", image: washington },
  ];

  return (
    <main>
      {/* Hero Section */}
      <div className="w-full min-h-[450px] sm:min-h-[550px] md:min-h-[650px] lg:h-[calc(100vh-64px)] relative overflow-hidden mt-[64px]">
        {/* Background Image Grid - Responsive */}
        <div className="absolute right-0 top-[160px] sm:top-[180px] md:top-[220px] lg:top-[280px] flex justify-start items-center gap-2 sm:gap-3 md:gap-4 lg:gap-8 scale-[0.5] sm:scale-[0.65] md:scale-[0.8] lg:scale-100 origin-right">
          {/* Column 0 - 1 image */}
          <div className="w-48 sm:w-48 md:w-64 lg:w-72 flex flex-col justify-start items-start gap-2 sm:gap-3 md:gap-4 lg:gap-8">
            <ImageCard venueIndex={0} delay={700} />
          </div>

          {/* Column 1 - 2 images */}
          <div className="w-48 sm:w-48 md:w-64 lg:w-72 flex flex-col justify-start items-start gap-2 sm:gap-3 md:gap-4 lg:gap-8">
            <ImageCard venueIndex={1} delay={800} />
            <ImageCard venueIndex={2} delay={1000} />
          </div>

          {/* Column 2 - 3 images - hidden on smallest screens */}
          <div className="hidden sm:flex w-64 sm:w-64 md:w-64 lg:w-72 flex-col justify-start items-start gap-2 sm:gap-3 md:gap-4 lg:gap-8">
            <ImageCard venueIndex={3} delay={600} />
            <ImageCard venueIndex={4} delay={850} />
            <ImageCard venueIndex={5} delay={1050} />
          </div>
        </div>

        {/* Content Container */}
        <div className="w-full flex justify-center items-start relative z-10">
          <div className="w-full max-w-[1280px] flex flex-col justify-start items-start gap-2.5 pt-8 sm:pt-12 md:pt-16 lg:pt-24 px-4 sm:px-6 lg:px-8">
            <motion.div
              className="w-full max-w-[692px] flex flex-col justify-start items-start gap-6 sm:gap-8 md:gap-10 lg:gap-12"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <div className="flex flex-col gap-3 sm:gap-4">
                {/* Text Content */}
                <div className="flex flex-col justify-start items-start gap-3 sm:gap-4">
                  <motion.h1
                    className="text-h2 sm:text-h1 text-landing-primary"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                  >
                    Find and secure your corporate event venue
                  </motion.h1>
                  <motion.div
                    className="w-full max-w-[473px] text-body text-neutral-700"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  >
                    Give us your event brief. Nowadays uses AI to identify and contact relevant venues, then organizes
                    the responses so you can compare real proposals in one place. Our managed approach pairs that
                    automation with event specialists who help interpret fit, exceptions, and tradeoffs.
                  </motion.div>
                </div>
              </div>

              {/* Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row justify-start items-center gap-3"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <Button
                  variant="primary"
                  size="lg"
                  arrow
                  onClick={() => router.push("/create")}
                  className="w-full sm:w-auto"
                >
                  Start your venue brief
                </Button>
                <Button
                  variant="outline"
                  color="neutral"
                  size="lg"
                  leftIcon={<PlayIcon />}
                  onClick={() => openModal("video")}
                  className="w-full sm:w-auto"
                >
                  Watch Video
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      <section aria-labelledby="venue-brief-heading" className="py-12 sm:py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl space-y-5"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <h2 id="venue-brief-heading" className="text-h2 text-landing-primary">
              Start with a useful event brief
            </h2>
            <p className="text-body-lg leading-relaxed text-landing-secondary">Give every venue one brief:</p>
          </motion.div>

          <motion.ul
            className="mt-8 grid max-w-4xl gap-6 sm:gap-8"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{
              duration: 0.7,
              delay: 0.1,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            {venueBriefItems.map((item) => (
              <li key={item.label} className="flex items-start gap-4">
                <div className="mt-1 shrink-0">
                  <CheckCircle aria-hidden="true" className="h-5 w-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-body-lg font-semibold text-landing-primary">{item.label}</h3>
                  <p className="text-body leading-relaxed text-landing-secondary">{item.description}</p>
                </div>
              </li>
            ))}
          </motion.ul>

          <motion.p
            className="mt-6 max-w-3xl text-body leading-relaxed text-landing-secondary"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            A request for proposal (RFP) is a venue bid request. Upload or paste yours.
          </motion.p>
        </div>
      </section>

      <section aria-labelledby="sourcing-workflow-heading" className="py-12 sm:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-2xl"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <h2 id="sourcing-workflow-heading" className="text-h2 text-landing-primary">
              How Nowadays supports venue sourcing
            </h2>
          </motion.div>

          <ol className="mt-10 grid gap-x-14 gap-y-8 md:grid-cols-2 lg:mt-14">
            {sourcingSteps.map((step, index) => (
              <motion.li
                key={step.title}
                className="grid grid-cols-[auto_1fr] gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-violet-200 text-tag-md font-semibold text-primary"
                >
                  {index + 1}
                </span>
                <p className="text-body leading-relaxed text-landing-secondary">
                  <strong className="font-semibold text-landing-primary">{step.title}</strong> {step.description}
                </p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <section aria-labelledby="proposal-comparison-heading" className="py-12 sm:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:gap-16">
            <motion.div
              className="space-y-5"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <h2 id="proposal-comparison-heading" className="text-h2 text-landing-primary">
                Compare the whole venue proposal
              </h2>
              <p className="text-body-lg leading-relaxed text-landing-secondary">
                Compare full event cost and fit instead of the room rate alone.
              </p>
              <p className="rounded-xl border-2 border-violet-100 p-5 text-body leading-relaxed text-landing-secondary">
                For finalists, conduct a site inspection, an in-person walk-through. Trace the attendee route from
                arrival through registration, meetings, meals, and guest rooms. Check sightlines, sound, accessibility,
                restrooms, lighting, Wi-Fi, service routes, and the on-site team.
              </p>
            </motion.div>

            <motion.dl
              className="border-y border-violet-100"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.7,
                delay: 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              {proposalCriteria.map((criterion) => (
                <div
                  key={criterion.term}
                  className="grid gap-2 border-b border-violet-100 py-5 last:border-b-0 sm:grid-cols-[minmax(150px,0.35fr)_minmax(0,1fr)] sm:gap-6"
                >
                  <dt className="text-body font-semibold text-landing-primary">{criterion.term}</dt>
                  <dd className="text-body leading-relaxed text-landing-secondary">{criterion.description}</dd>
                </div>
              ))}
            </motion.dl>
          </div>
        </div>
      </section>

      {/* Main Content Section - Left-aligned and Linear */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 sm:space-y-10 md:space-y-12">
            <div className="space-y-6 sm:space-y-8">
              <div className="space-y-4">
                <h2 className="text-h2 text-landing-primary">Venues for the corporate events you run</h2>
                <p className="text-body-lg leading-relaxed text-landing-secondary">
                  We source venues for corporate retreats and offsites, all-company gatherings, sales kickoffs,
                  executive retreats, board meetings, and incentive trips. The same sourcing process works across these
                  formats, while the brief changes the spaces, room pattern, and services a venue must provide.
                </p>
              </div>

              <div className="grid gap-6 sm:gap-8">
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 mt-1">
                    <CheckCircle aria-hidden="true" className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-body-lg font-semibold text-landing-primary">
                      All-Company Gatherings & Sales Kickoffs (SKO)
                    </h3>
                    <p className="text-body leading-relaxed text-landing-secondary">
                      Energize your team with inspiring company-wide events or SKOs that align everyone around key goals
                      and messages.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="shrink-0 mt-1">
                    <CheckCircle aria-hidden="true" className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-body-lg font-semibold text-landing-primary">Corporate Retreats & Offsites</h3>
                    <p className="text-body leading-relaxed text-landing-secondary">
                      We design offsites that spark creativity, deepen team connection, and deliver lasting
                      impact—tailored to your company's culture and objectives.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="shrink-0 mt-1">
                    <CheckCircle aria-hidden="true" className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-body-lg font-semibold text-landing-primary">Executive Retreats</h3>
                    <p className="text-body leading-relaxed text-landing-secondary">
                      Highly personalized leadership getaways focused on strategic thinking, alignment, and renewal.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="shrink-0 mt-1">
                    <CheckCircle aria-hidden="true" className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-body-lg font-semibold text-landing-primary">Board Meetings</h3>
                    <p className="text-body leading-relaxed text-landing-secondary">
                      Flawless logistics and white-glove service to ensure your board stays focused on decisions—not
                      details.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="shrink-0 mt-1">
                    <CheckCircle aria-hidden="true" className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-body-lg font-semibold text-landing-primary">Sales Kickoffs</h3>
                    <p className="text-body leading-relaxed text-landing-secondary">
                      High-energy, purpose-driven events that build momentum, unify your team, and drive performance for
                      the year ahead.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="shrink-0 mt-1">
                    <CheckCircle aria-hidden="true" className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-body-lg font-semibold text-landing-primary">Incentive Trips</h3>
                    <p className="text-body leading-relaxed text-landing-secondary">
                      Unforgettable experiences that reward top performers and reinforce company values in extraordinary
                      settings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-6 pt-4 md:grid-cols-2">
                <figure className="overflow-hidden rounded-xl border-2 border-violet-100 bg-white">
                  <img
                    src="/landing/venue-sourcing/corporate-retreat-team-activity.svg"
                    alt="Coworkers playing lawn games at a resort offsite"
                    className="aspect-3/2 w-full object-cover"
                    loading="lazy"
                  />
                  <figcaption className="p-5 text-body leading-relaxed text-landing-secondary">
                    For retreats and offsites, outdoor areas can give the group a useful change of pace between working
                    sessions. Their availability belongs in the same space-by-time plan as breakout rooms and meal
                    venues.
                  </figcaption>
                </figure>

                <figure className="overflow-hidden rounded-xl border-2 border-violet-100 bg-white">
                  <img
                    src="/landing/venue-sourcing/corporate-offsite-group-dinner.svg"
                    alt="Corporate team sharing an outdoor dinner at a resort"
                    className="aspect-3/2 w-full object-cover"
                    loading="lazy"
                  />
                  <figcaption className="p-5 text-body leading-relaxed text-landing-secondary">
                    A welcome dinner or closing meal may need a courtyard, terrace, or private dining area outside the
                    daytime meeting footprint. Include that time window in the brief so venues can confirm the space
                    rather than assume it is available.
                  </figcaption>
                </figure>
              </div>

              <p className="text-body-lg leading-relaxed text-landing-secondary">
                Some briefs call for a luxury beach hotel such as Hotel Casa del Mar in Santa Monica. Others point
                toward resorts such as RIMBA by AYANA Bali or Four Seasons Resort Maui at Wailea, or a historic property
                such as San Domenico Palace, A Four Seasons Hotel in Taormina. These examples show the range of
                settings. A live shortlist depends on your dates, headcount, space, room needs, and budget.
              </p>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <h2 className="text-h2 text-landing-primary">Pre-built Packages</h2>
              <p className="text-body-lg text-landing-secondary">
                Want it all handled? Our corporate retreat packages include flights, venue, ground transportation,
                activities, and full itinerary & planning.
              </p>
              <p className="text-body-lg text-landing-primary font-semibold">All done-for-you.</p>
            </div>

            {/* Pre-built Packages */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
              <div
                className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col cursor-pointer"
                onClick={() => openModal("maui-details")}
              >
                <div className="h-36 sm:h-40 md:h-48 w-full mb-4 rounded-lg overflow-hidden relative">
                  {/* Add loading state here similar to above */}
                  <img src={maui.src} alt="Maui retreat location" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-body-lg font-semibold text-landing-primary mb-4">Maui Adventure Offsite</h3>
                <p className="text-landing-primary mb-2">
                  <strong>Location:</strong> Lahaina, Maui, Hawaii
                </p>
                <p className="text-landing-primary mb-2">
                  <strong>Duration:</strong> 5 days / 4 nights
                </p>
                <p className="text-landing-primary mb-4">
                  <strong>Ideal for:</strong> Team bonding, leadership retreats, or incentive trips
                </p>

                <p className="text-landing-secondary text-sm grow">
                  Day 1: Arrival & coastal dining
                  <br />
                  Day 2: Island thrills & sunset cruise
                  <br />
                  Day 3: Nature immersion & beachfront dining
                  <br />
                  Day 4: Iconic Hawaiian culture
                  <br />
                  Day 5: Wrap-up & departure
                </p>

                <Button variant="outline" size="sm" className="mt-4">
                  Learn More
                </Button>
              </div>

              <div
                className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col cursor-pointer"
                onClick={() => openModal("costa-rica-details")}
              >
                <div className="h-36 sm:h-40 md:h-48 w-full mb-4 rounded-lg overflow-hidden relative">
                  {/* Add loading state here similar to above */}
                  <img src={costarica.src} alt="Costa Rica retreat location" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-body-lg font-semibold text-landing-primary mb-4">Costa Rica Nature Reset</h3>
                <p className="text-landing-primary mb-2">
                  <strong>Location:</strong> Hacienda Pinilla, Guanacaste, Costa Rica
                </p>
                <p className="text-landing-primary mb-2">
                  <strong>Duration:</strong> 4 days / 3 nights
                </p>
                <p className="text-landing-primary mb-4">
                  <strong>Ideal for:</strong> Strategic planning retreats, wellness offsites, and small team getaways
                </p>

                <p className="text-landing-secondary text-sm grow">
                  Day 1: Arrival & reset
                  <br />
                  Day 2: Adventure & strategy
                  <br />
                  Day 3: Movement, team building & sunset moments
                  <br />
                  Day 4: Recharge & departure
                </p>

                <Button variant="outline" size="sm" className="mt-4">
                  Learn More
                </Button>
              </div>

              <div
                className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col cursor-pointer"
                onClick={() => openModal("barcelona-details")}
              >
                <div className="h-36 sm:h-40 md:h-48 w-full mb-4 rounded-lg overflow-hidden relative">
                  {/* Add loading state here similar to above */}
                  <img src={barcelona2.src} alt="Barcelona retreat location" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-body-lg font-semibold text-landing-primary mb-4">Barcelona Creative Immersion</h3>
                <p className="text-landing-primary mb-2">
                  <strong>Location:</strong> Eixample, Barcelona, Spain
                </p>
                <p className="text-landing-primary mb-2">
                  <strong>Duration:</strong> 7 days / 6 nights
                </p>
                <p className="text-landing-primary mb-4">
                  <strong>Ideal for:</strong> International team retreats, hybrid work getaways, and cultural immersions
                </p>

                <p className="text-landing-secondary text-sm grow">
                  Day 1: Arrival
                  <br />
                  Day 2: City & culture
                  <br />
                  Day 3-6: Work & play hybrid
                  <br />
                  Day 7: Departure
                </p>

                <Button variant="outline" size="sm" className="mt-4">
                  Learn More
                </Button>
              </div>

              <div
                className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col cursor-pointer"
                onClick={() => openModal("napa-details")}
              >
                <div className="h-36 sm:h-40 md:h-48 w-full mb-4 rounded-lg overflow-hidden relative">
                  {/* Add loading state here similar to above */}
                  <img src={napa.src} alt="Napa retreat location" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-body-lg font-semibold text-landing-primary mb-4">
                  Napa Strategy & Connection Retreat
                </h3>
                <p className="text-landing-primary mb-2">
                  <strong>Location:</strong> Napa Valley, CA
                </p>
                <p className="text-landing-primary mb-2">
                  <strong>Duration:</strong> 4 days / 3 nights
                </p>
                <p className="text-landing-primary mb-4">
                  <strong>Ideal for:</strong> Executive offsites, strategic planning retreats, or leadership gatherings
                </p>
                <p className="text-landing-secondary text-sm grow">
                  Vineyard views, luxury cottages, wine blending, and wellness activities in the heart of Napa.
                </p>
                <Button variant="outline" size="sm" className="mt-4">
                  Learn More
                </Button>
              </div>

              <div
                className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col cursor-pointer"
                onClick={() => openModal("sandiego-details")}
              >
                <div className="h-36 sm:h-40 md:h-48 w-full mb-4 rounded-lg overflow-hidden relative">
                  {/* Add loading state here similar to above */}
                  <img src={sandiego.src} alt="San Diego retreat location" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-body-lg font-semibold text-landing-primary mb-4">Team Retreat in San Diego</h3>
                <p className="text-landing-primary mb-2">
                  <strong>Location:</strong> San Diego, California
                </p>
                <p className="text-landing-primary mb-2">
                  <strong>Duration:</strong> 4 days / 3 nights
                </p>
                <p className="text-landing-primary mb-4">
                  <strong>Ideal for:</strong> Small team retreats, hybrid work gatherings, or mission-driven company
                  offsites
                </p>
                <p className="text-landing-secondary text-sm grow">
                  Oceanfront resort, charity mini golf, winery visit, and bonfire farewell dinner.
                </p>
                <Button variant="outline" size="sm" className="mt-4">
                  Learn More
                </Button>
              </div>

              <div
                className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col cursor-pointer"
                onClick={() => openModal("cancun-details")}
              >
                <div className="h-36 sm:h-40 md:h-48 w-full mb-4 rounded-lg overflow-hidden relative">
                  {/* Add loading state here similar to above */}
                  <img src={cancun2.src} alt="Cancun retreat location" className="w-full h-full object-cover" />
                </div>
                <h3 className="text-body-lg font-semibold text-landing-primary mb-4">Cancun Company Retreat</h3>
                <p className="text-landing-primary mb-2">
                  <strong>Location:</strong> Playa del Carmen, Mexico
                </p>
                <p className="text-landing-primary mb-2">
                  <strong>Duration:</strong> 4 days / 3 nights
                </p>
                <p className="text-landing-primary mb-4">
                  <strong>Ideal for:</strong> Corporate retreats, team offsites, or incentive trips
                </p>
                <p className="text-landing-secondary text-sm grow">
                  Beachfront luxury, mixology class, kayak tour, tequila tasting, and curated group meals.
                </p>
                <Button variant="outline" size="sm" className="mt-4">
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Locations Section */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-24">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="text-center flex flex-col gap-4 sm:gap-6 mb-12 sm:mb-16">
            <h2 className="text-h2 text-landing-primary">Top Destinations Worldwide</h2>
            <p className="text-body-lg text-landing-secondary max-w-[530px] mx-auto">
              From historic European cities to tropical retreats, we have vetted venues in the world's most desirable
              destinations for corporate events.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 max-w-7xl mx-auto">
            {topLocations.map((location, index) => {
              const [isLoading, setIsLoading] = useState(false);
              const [hasError, setHasError] = useState(false);
              const timeoutRef = useRef<NodeJS.Timeout>();

              const handleImageLoad = () => {
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }
                setIsLoading(false);
              };

              const handleImageError = () => {
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }
                setIsLoading(false);
                setHasError(true);
              };

              const handleImageLoadStart = () => {
                timeoutRef.current = setTimeout(() => {
                  setIsLoading(true);
                }, 250); // Show loading after 250ms
              };

              useEffect(() => {
                return () => {
                  if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                  }
                };
              }, []);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group cursor-pointer"
                >
                  <div className="w-full h-48 sm:h-56 md:h-64 relative rounded-xl overflow-hidden border-2 border-violet-100 hover:border-violet-300 transition-all duration-300 hover:shadow-lg">
                    {/* Loading overlay */}
                    {isLoading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100">
                        <LoadingDog size={40} />
                      </div>
                    )}

                    {/* Error overlay */}
                    {hasError && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100">
                        <div className="text-center text-gray-500">
                          <div className="text-sm">Failed to load image</div>
                        </div>
                      </div>
                    )}

                    {/* Background Image */}
                    <img
                      src={typeof location.image === "string" ? location.image : location.image.src}
                      alt={`${location.city} venue`}
                      className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 transition-opacity duration-200 ${
                        isLoading ? "opacity-0" : "opacity-100"
                      }`}
                      onLoadStart={handleImageLoadStart}
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />

                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/20 to-transparent" />

                    {/* City Info - Bottom Left */}
                    <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3">
                      <div className="flex flex-col gap-0.5">
                        <div className="text-h4 text-white font-semibold">{location.city}</div>
                        {location.country && <div className="text-tag-md text-white/80">{location.country}</div>}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <Testimonial variant="single" person="yeon" title="What corporate planners say" />

      {/* Custom CTA Section */}
      <section
        aria-labelledby="venue-sourcing-cta-heading"
        className="w-full flex flex-col gap-6 md:gap-8 md:text-center py-12 md:py-24"
      >
        <div className="text-center">
          <h2
            id="venue-sourcing-cta-heading"
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-left md:text-center"
          >
            Start finding your venue
          </h2>
          <p className="text-lg text-gray-600 md:max-w-lg md:mx-auto mb-8 text-left md:text-center">
            Send us your location, dates, headcount, budget, and space needs. We will use the brief to begin sourcing
            and organize the proposal details for your review.
          </p>
          <div className="flex md:justify-center">
            <Button variant="primary" size="lg" arrow onClick={() => router.push("/create")}>
              Start your venue brief
            </Button>
          </div>
        </div>
      </section>

      {/* Unified Modals */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Book a Demo"
        subtitle="Schedule a 30-minute demo with our team"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your demo with our team"
      />

      <UnifiedModal
        type="content"
        isOpen={isModalOpen("video")}
        onClose={() => closeModal("video")}
        title="Watch Demo Video"
        subtitle="See Nowadays in action"
        size="lg"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-4">
          <iframe
            className="w-full h-[70vh] rounded-xl"
            src="https://www.youtube.com/embed/SS2bcogWkeE"
            title="Nowadays - AI for Event Planning (YC S23)"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
      </UnifiedModal>

      {/* Maui Package Details Modal */}
      <UnifiedModal
        type="content"
        isOpen={isModalOpen("maui-details")}
        onClose={() => closeModal("maui-details")}
        title="Maui Adventure Offsite - Detailed Itinerary"
        subtitle="5 days / 4 nights of adventure and team bonding in paradise"
        size="md"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-6 space-y-6">
          <img src={maui.src} alt="Maui beach view" className="w-full h-96 object-cover rounded-lg" />
          <div className="space-y-4">
            <h3 className="text-h4 text-landing-primary font-semibold">Package Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-body text-landing-secondary">
              <div>
                <p>
                  <strong>Location:</strong> Lahaina, Maui, Hawaii
                </p>
                <p>
                  <strong>Accommodation:</strong> Private oceanfront villa near Napili Bay
                </p>
                <p>
                  <strong>Duration:</strong> 5 days / 4 nights
                </p>
                <p>
                  <strong>Ideal for:</strong> Team bonding, leadership retreats, or incentive trips
                </p>
              </div>
              <div>
                <p>
                  <strong>Airport:</strong> Kahului Airport (OGG)
                </p>
                <p>
                  <strong>Transport:</strong> Seamless group transport via rental cars
                </p>
                <p>
                  <strong>Meals:</strong> All dining experiences included
                </p>
                <p>
                  <strong>Coordination:</strong> Fully pre-paid and professionally coordinated
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Highlights</h3>
            <ul className="text-body text-landing-secondary space-y-2 pl-4">
              <li>✈️ Arrival at Kahului Airport (OGG) with seamless group transport via rental cars</li>
              <li>🏡 Stay at a premium beachfront villa with easy access to Lahaina and Napili</li>
              <li>🌴 Adventure-packed itinerary with a mix of rest, bonding, and Hawaiian culture</li>
              <li>✅ All excursions fully pre-paid and professionally coordinated</li>
            </ul>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Daily Itinerary</h3>

            <div className="space-y-4">
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 1 – Arrival & Coastal Dining</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Arrival at OGG with rental car pickup</li>
                  <li>Lunch at Mala's Ocean Tavern (vegetarian/gluten-free friendly)</li>
                  <li>Dinner at a local Lahaina favorite with ocean views</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">
                  Day 2 – Island Thrills & Sunset Cruise
                </h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Morning brunch at a scenic plantation restaurant</li>
                  <li>Midday ATV excursion through Maui's rugged trails</li>
                  <li>Evening sunset catamaran dinner cruise with island-style cuisine and live music</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">
                  Day 3 – Nature Immersion & Beachfront Dining
                </h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Morning coffee run to a local café</li>
                  <li>Private guided hike to waterfalls with on-the-go lunch</li>
                  <li>Group dinner at a renowned oceanside restaurant</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 4 – Iconic Hawaiian Culture</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Brunch at Maui's famous fish house</li>
                  <li>Evening celebration at a traditional luau with entertainment and buffet dinner</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 5 – Wrap-Up & Departure</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Walk-in breakfast at a beachside eatery</li>
                  <li>Group departures from OGG</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h4 className="text-body-lg font-semibold text-landing-primary">Activities Included</h4>
              <ul className="text-body text-landing-secondary space-y-2 pl-4">
                <li>Off-road ATV adventure through tropical terrain</li>
                <li>Private waterfall hike with transportation and lunch</li>
                <li>Sunset catamaran cruise with dinner and drinks</li>
                <li>Traditional Hawaiian luau with cultural performances</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Who This Is For</h3>
            <p className="text-body text-landing-secondary">
              Perfect for teams seeking adventure and cultural immersion in a luxury setting. Ideal for team bonding,
              leadership retreats, or incentive trips where you want to combine thrilling activities with authentic
              Hawaiian experiences and premium accommodations.{" "}
              <Link
                href="/case-studies/the-imbue-offsite-that-made-room-for-everything-that-matters"
                className="text-primary hover:underline"
              >
                Check out Imbue's Maui offsite
              </Link>{" "}
              to see how another team made the most of their time in paradise.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <Button
              onClick={() => {
                closeModal("maui-details");
                openModal("demo");
              }}
              className="w-full"
            >
              Book This Package
            </Button>
          </div>
        </div>
      </UnifiedModal>

      {/* Costa Rica Package Details Modal */}
      <UnifiedModal
        type="content"
        isOpen={isModalOpen("costa-rica-details")}
        onClose={() => closeModal("costa-rica-details")}
        title="Costa Rica Nature Reset - Detailed Itinerary"
        subtitle="4 days / 3 nights of strategic planning and wellness in paradise"
        size="md"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-6 space-y-6">
          <img src={costarica.src} alt="Costa Rica beach view" className="w-full h-96 object-cover rounded-lg" />
          <div className="space-y-4">
            <h3 className="text-h4 text-landing-primary font-semibold">Package Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-body text-landing-secondary">
              <div>
                <p>
                  <strong>Location:</strong> Hacienda Pinilla, Guanacaste, Costa Rica
                </p>
                <p>
                  <strong>Accommodation:</strong> Two luxury villas near Playa Avellanas
                </p>
                <p>
                  <strong>Duration:</strong> 4 days / 3 nights
                </p>
                <p>
                  <strong>Ideal for:</strong> Strategic planning retreats, wellness offsites, and small team getaways
                </p>
              </div>
              <div>
                <p>
                  <strong>Transport:</strong> Group transportation with staggered airport shuttles
                </p>
                <p>
                  <strong>Meals:</strong> Catered local meals and offsite restaurant reservations
                </p>
                <p>
                  <strong>Coordination:</strong> Activities prepaid and allergy-friendly meals arranged
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Highlights</h3>
            <ul className="text-body text-landing-secondary space-y-2 pl-4">
              <li>✈️ Group transportation with staggered airport shuttles and on-site rental car delivery</li>
              <li>
                🏡 Two spacious villas with private bedrooms, on-site concierge support, and golf carts for easy
                movement around the resort
              </li>
              <li>🌿 Schedule balances deep work, movement, nature, and bonding</li>
              <li>
                📸 Includes professional sunset photo shoot, guided kayaking tour, yoga class, and offsite dinners
              </li>
              <li>✅ Activities prepaid and allergy-friendly meals arranged</li>
            </ul>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Daily Itinerary</h3>

            <div className="space-y-4">
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 1 – Arrival & Reset</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Airport pickups arranged via shuttle</li>
                  <li>Fresh welcome snacks and local juice upon arrival</li>
                  <li>Light movement: group pickleball to unwind</li>
                  <li>Catered Costa Rican dinner at the villa</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 2 – Adventure & Strategy</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Morning kayaking tour through the Tamarindo mangrove estuary</li>
                  <li>Afternoon work sessions on product planning and business strategy</li>
                  <li>Locally-sourced lunch and snacks provided onsite</li>
                  <li>Surf & turf dinner followed by an optional evening work block</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">
                  Day 3 – Movement, Team Building & Sunset Moments
                </h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Private Vinyasa yoga session at the villa</li>
                  <li>Light work block followed by Costa Rican lunch (Chifrijito)</li>
                  <li>Pickleball tournament and downtime at the villa</li>
                  <li>Golden hour beach photoshoot with local photographer</li>
                  <li>Group dinner at Pangas Beach Club</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 4 – Recharge & Departure</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Continental breakfast shared with any +1s</li>
                  <li>Final group pickleball session</li>
                  <li>Group shuttles to airport for staggered departures</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h4 className="text-body-lg font-semibold text-landing-primary">Activities Included</h4>
              <ul className="text-body text-landing-secondary space-y-2 pl-4">
                <li>🚣 Guided mangrove kayaking with wildlife spotting</li>
                <li>🧘‍♀️ Private beginner/intermediate yoga session at the villa</li>
                <li>📸 Sunset beach photoshoot with professional photographer</li>
                <li>🏓 Pickleball sessions at nearby courts (equipment rentals arranged)</li>
                <li>🍽️ Catered local meals and offsite restaurant reservations</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Who This Is For</h3>
            <p className="text-body text-landing-secondary">
              Great for small teams looking for a calm, tropical setting to recharge, align on goals, and strengthen
              connections—without sacrificing comfort or productivity.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <Button
              onClick={() => {
                closeModal("costa-rica-details");
                openModal("demo");
              }}
              className="w-full"
            >
              Book This Package
            </Button>
          </div>
        </div>
      </UnifiedModal>

      {/* Barcelona Package Details Modal */}
      <UnifiedModal
        type="content"
        isOpen={isModalOpen("barcelona-details")}
        onClose={() => closeModal("barcelona-details")}
        title="Barcelona Creative Immersion - Detailed Itinerary"
        subtitle="7 days / 6 nights of culture, creativity, and collaboration in Catalonia"
        size="md"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-6 space-y-6">
          <img src={barcelona2.src} alt="Barcelona cityscape" className="w-full h-96 object-cover rounded-lg" />
          <div className="space-y-4">
            <h3 className="text-h4 text-landing-primary font-semibold">Package Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-body text-landing-secondary">
              <div>
                <p>
                  <strong>Location:</strong> Eixample, Barcelona, Spain
                </p>
                <p>
                  <strong>Accommodation:</strong> Historic private villa off Passeig de Gràcia
                </p>
                <p>
                  <strong>Duration:</strong> 7 days / 6 nights
                </p>
                <p>
                  <strong>Ideal for:</strong> International team retreats, hybrid work getaways, and cultural immersions
                </p>
              </div>
              <div>
                <p>
                  <strong>Airport:</strong> Barcelona El Prat (BCN)
                </p>
                <p>
                  <strong>Work Space:</strong> Dedicated co-working suite in central Barcelona
                </p>
                <p>
                  <strong>Coordination:</strong> Fully coordinated arrival logistics and pre-paid experiences
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Highlights</h3>
            <ul className="text-body text-landing-secondary space-y-2 pl-4">
              <li>
                🏡 Private residence in a charming passageway, walking distance to Gaudí architecture and vibrant cafés
              </li>
              <li>💼 Dedicated co-working suite in central Barcelona with boardroom-style setup and fast Wi-Fi</li>
              <li>🏛️ Iconic sightseeing at Park Güell and Sagrada Familia with timed entries</li>
              <li>🍷 Guided 4-stop tapas crawl with drinks and cultural storytelling</li>
              <li>🍫 Hands-on chocolate making workshop in Gràcia</li>
              <li>🍽️ Daily pre-reserved group dinners with set menus or a la carte options</li>
              <li>
                ✈️ Fully coordinated arrival logistics, pre-paid experiences, and support for large group movement
              </li>
            </ul>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Daily Itinerary</h3>

            <div className="space-y-4">
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 1 – Arrival</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Land at BCN and check into a centrally located heritage home</li>
                  <li>Settle in and explore the local neighborhood</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 2 – City & Culture</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Morning visit to Park Güell</li>
                  <li>Afternoon towers tour of Sagrada Familia</li>
                  <li>Group dinner at a local Catalan bistro</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 3–6 – Work & Play Hybrid</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Daily access to professional coworking space near Plaza Universitat</li>
                  <li>Evenings filled with curated group experiences:</li>
                  <li>Dinner at Casa Lolea</li>
                  <li>Set-menu meal at La Selva</li>
                  <li>Tapas crawl through Poble Sec and Sant Antoni</li>
                  <li>Chocolate bean-to-bar workshop at a local atelier</li>
                  <li>Final dinner at El Nacional's iconic Braseria</li>
                </ul>
              </div>

              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 7 – Departure</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Free morning or local exploring</li>
                  <li>Group returns via BCN departures</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h4 className="text-body-lg font-semibold text-landing-primary">Activities Included</h4>
              <ul className="text-body text-landing-secondary space-y-2 pl-4">
                <li>🏡 Reserved entry to Park Güell and Sagrada Familia (with tower access and audio guides)</li>
                <li>🍷 Guided 4-stop tapas crawl with drinks and cultural storytelling</li>
                <li>🍫 Hands-on chocolate making workshop in Gràcia</li>
                <li>🍽️ Daily pre-reserved group dinners with set menus or a la carte options</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Who This Is For</h3>
            <p className="text-body text-landing-secondary">
              Ideal for remote-first teams or international companies looking for a destination that combines
              productivity with culture. This offsite balances deep work blocks with curated dining, team bonding, and
              iconic sights.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <Button
              onClick={() => {
                closeModal("barcelona-details");
                openModal("demo");
              }}
              className="w-full"
            >
              Book This Package
            </Button>
          </div>
        </div>
      </UnifiedModal>

      {/* Napa Package Details Modal */}
      <UnifiedModal
        type="content"
        isOpen={isModalOpen("napa-details")}
        onClose={() => closeModal("napa-details")}
        title="Napa Strategy & Connection Retreat - Detailed Itinerary"
        subtitle="4 days / 3 nights of strategy, connection, and sensory experiences in Napa Valley"
        size="md"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-6 space-y-6">
          <img src={napa.src} alt="Napa Valley view" className="w-full h-96 object-cover rounded-lg" />
          <div className="space-y-4">
            <h3 className="text-h4 text-landing-primary font-semibold">Package Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-body text-landing-secondary">
              <div>
                <p>
                  <strong>Location:</strong> Napa Valley, CA
                </p>
                <p>
                  <strong>Accommodation:</strong> Luxury cottages and residences
                </p>
                <p>
                  <strong>Duration:</strong> 4 days / 3 nights
                </p>
                <p>
                  <strong>Ideal for:</strong> Executive offsites, strategic planning retreats, or leadership gatherings
                </p>
              </div>
              <div>
                <p>
                  <strong>Airport:</strong> SFO / OAK (90 min drive)
                </p>
                <p>
                  <strong>Transport:</strong> Charter shuttle or individual reimbursements
                </p>
                <p>
                  <strong>Meals:</strong> Curated private dinners + local partner catering
                </p>
                <p>
                  <strong>Coordination:</strong> Contract + planning via Nowadays
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Highlights</h3>
            <ul className="text-body text-landing-secondary space-y-2 pl-4">
              <li>🧠 Work sessions with scenic vineyard views</li>
              <li>🛏️ Luxury cottages with fireplaces and patios</li>
              <li>🍷 Wine blending, hot air ballooning, cooking classes</li>
              <li>🚲 Biking trails and downtown Napa access</li>
              <li>🧘‍♀️ Wellness activities onsite</li>
            </ul>
          </div>
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Daily Itinerary</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 1 – Arrival & Connection</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Arrival at cottage-style accommodations</li>
                  <li>Welcome toast and scenic reception</li>
                  <li>Dinner with wine pairings</li>
                  <li>Fire pit hangout or wine & s'mores</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 2 – Strategy & Sensory Play</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Breakfast on private patio</li>
                  <li>Team sessions in indoor/outdoor space</li>
                  <li>Catered working lunch with wine sampling</li>
                  <li>Grape stomping competition</li>
                  <li>Private karaoke or open-mic social</li>
                  <li>Group dinner at winery</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 3 – Movement & Making</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Bike tour to downtown Napa or olive grove</li>
                  <li>Wine blending class or cooking experience</li>
                  <li>Outdoor yoga or spa block</li>
                  <li>Charcuterie-making or paint & sip</li>
                  <li>Farewell dinner with bonfire or awards</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 4 – Wrap-Up & Departure</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Leisurely breakfast</li>
                  <li>Optional final reflections or 1:1s</li>
                  <li>Departures</li>
                </ul>
              </div>
            </div>
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h4 className="text-body-lg font-semibold text-landing-primary">Activities Included</h4>
              <ul className="text-body text-landing-secondary space-y-2 pl-4">
                <li>🍷 Blind wine tasting competition</li>
                <li>🍇 Grape stomping</li>
                <li>🚲 Scenic bike tour</li>
                <li>🍽️ Private cooking experience</li>
                <li>🎤 Karaoke or open-mic social</li>
                <li>🧀 Charcuterie board–making workshop</li>
                <li>🧘‍♀️ Morning yoga or wellness session</li>
              </ul>
            </div>
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h4 className="text-body-lg font-semibold text-landing-primary">Optional Add-ons</h4>
              <ul className="text-body text-landing-secondary space-y-2 pl-4">
                <li>Hot air balloon sunrise ride</li>
                <li>Napa Valley Wine Train journey</li>
                <li>Kayak challenge with wine pit stops</li>
                <li>Themed "Amazing Race" across vineyards</li>
              </ul>
            </div>
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-h4 text-landing-primary font-semibold">Who This Is For</h3>
              <p className="text-body text-landing-secondary">
                Perfect for executive or department-level teams seeking both productivity and inspiration. Especially
                suited for groups needing space to align strategically while indulging in the rich culture and sensory
                experiences Napa has to offer.
              </p>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <Button
                onClick={() => {
                  closeModal("napa-details");
                  openModal("demo");
                }}
                className="w-full"
              >
                Book This Package
              </Button>
            </div>
          </div>
        </div>
      </UnifiedModal>

      {/* San Diego Package Details Modal */}
      <UnifiedModal
        type="content"
        isOpen={isModalOpen("sandiego-details")}
        onClose={() => closeModal("sandiego-details")}
        title="Team Retreat in San Diego - Detailed Itinerary"
        subtitle="4 days / 3 nights of oceanfront fun, team bonding, and local experiences"
        size="md"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-6 space-y-6">
          <img src={sandiego.src} alt="San Diego beach view" className="w-full h-96 object-cover rounded-lg" />
          <div className="space-y-4">
            <h3 className="text-h4 text-landing-primary font-semibold">Package Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-body text-landing-secondary">
              <div>
                <p>
                  <strong>Location:</strong> San Diego, California
                </p>
                <p>
                  <strong>Accommodation:</strong> Oceanfront resort
                </p>
                <p>
                  <strong>Duration:</strong> 4 days / 3 nights
                </p>
                <p>
                  <strong>Ideal for:</strong> Small team retreats, hybrid work gatherings, or mission-driven company
                  offsites
                </p>
              </div>
              <div>
                <p>
                  <strong>Airport:</strong> San Diego International Airport (SAN)
                </p>
                <p>
                  <strong>Transport:</strong> Individual rideshares and local transport
                </p>
                <p>
                  <strong>Meals:</strong> On-site dining, local restaurants, and catered meals
                </p>
                <p>
                  <strong>Coordination:</strong> Full itinerary support
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Highlights</h3>
            <ul className="text-body text-landing-secondary space-y-2 pl-4">
              <li>🌴 Oceanfront resort experience</li>
              <li>📸 Professional headshots</li>
              <li>🐾 Charity mini golf</li>
              <li>🍷 Local winery visit</li>
              <li>🔥 Bonfire farewell dinner</li>
            </ul>
          </div>
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Daily Itinerary</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 1 – Arrival & Downtown Fun</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Resort check-in</li>
                  <li>Dinner + bowling at Punch Bowl Social</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 2 – Strategy & Service</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Breakfast</li>
                  <li>Morning work session</li>
                  <li>Catered lunch onsite</li>
                  <li>Headshot session with photographer</li>
                  <li>Coronado Beach hangout</li>
                  <li>Charity Putt-Putt for Project Street Vet</li>
                  <li>Dinner in Little Italy</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 3 – Wine & Wind Down</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Breakfast + morning work session</li>
                  <li>Group lunch</li>
                  <li>Winery visit (Gianni Buonomo Vintners)</li>
                  <li>Puzzle Mania Pursuit</li>
                  <li>Farewell dinner + bonfire experience</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 4 – Departure</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Final group breakfast</li>
                  <li>Hotel check-out</li>
                </ul>
              </div>
            </div>
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h4 className="text-body-lg font-semibold text-landing-primary">Activities Included</h4>
              <ul className="text-body text-landing-secondary space-y-2 pl-4">
                <li>📸 Professional headshot session</li>
                <li>⛳ Charity mini golf</li>
                <li>🧩 Puzzle Mania Pursuit</li>
                <li>🍷 Winery experience</li>
                <li>🔥 Private bonfire night</li>
                <li>🌊 Beach hangout</li>
                <li>⛵ Party Deck Charter Boat (Optional Upgrade)</li>
              </ul>
            </div>
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-h4 text-landing-primary font-semibold">Who This Is For</h3>
              <p className="text-body text-landing-secondary">
                Perfect for purpose-driven teams looking to blend light strategic planning with meaningful activities,
                local experiences, and feel-good give-back moments. Ideal for startups and teams looking to reconnect in
                a relaxed yet productive coastal setting.
              </p>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <Button
                onClick={() => {
                  closeModal("sandiego-details");
                  openModal("demo");
                }}
                className="w-full"
              >
                Book This Package
              </Button>
            </div>
          </div>
        </div>
      </UnifiedModal>

      {/* Cancun Package Details Modal */}
      <UnifiedModal
        type="content"
        isOpen={isModalOpen("cancun-details")}
        onClose={() => closeModal("cancun-details")}
        title="Cancun Company Retreat – Detailed Itinerary"
        subtitle="4 days / 3 nights of team bonding, beachfront luxury, and unforgettable experiences"
        size="md"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-6 space-y-6">
          <img src={cancun2.src} alt="Cancun beach view" className="w-full h-96 object-cover rounded-lg" />
          <div className="space-y-4">
            <h3 className="text-h4 text-landing-primary font-semibold">Package Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-body text-landing-secondary">
              <div>
                <p>
                  <strong>Location:</strong> Playa del Carmen, Mexico
                </p>
                <p>
                  <strong>Accommodation:</strong> 5-star beachfront resort
                </p>
                <p>
                  <strong>Duration:</strong> 4 days / 3 nights
                </p>
                <p>
                  <strong>Ideal for:</strong> Corporate retreats, team offsites, or incentive trips
                </p>
              </div>
              <div>
                <p>
                  <strong>Airport:</strong> Cancun International Airport (CUN)
                </p>
                <p>
                  <strong>Transport:</strong> Roundtrip airport shuttles included
                </p>
                <p>
                  <strong>Meals:</strong> Breakfast, lunch, and curated dinners included
                </p>
                <p>
                  <strong>Coordination:</strong> Fully pre-paid and professionally managed
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Highlights</h3>
            <ul className="text-body text-landing-secondary space-y-2 pl-4">
              <li>✈️ Smooth airport arrivals with shuttle coordination</li>
              <li>🏖️ Oceanfront venue with meeting rooms and private dining</li>
              <li>🧑‍🍳 Mixology class, kayak tour, and tequila tasting</li>
              <li>🍱 Curated group meals at on-site restaurants</li>
              <li>💼 Private meeting rooms with coffee breaks</li>
              <li>✅ All experiences organized and prepaid</li>
            </ul>
          </div>
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-h4 text-landing-primary font-semibold">Daily Itinerary</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 1 – Arrival & Welcome Dinner</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Group arrivals at Cancun International Airport (CUN)</li>
                  <li>Shuttle transfers to the resort</li>
                  <li>Afternoon lunch</li>
                  <li>Mixology class</li>
                  <li>Welcome dinner and reception</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 2 – Kayaking & Collaboration</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Breakfast</li>
                  <li>Guided kayak tour from Playa</li>
                  <li>Full-day team meeting with coffee breaks and lunch</li>
                  <li>Private dinner at a renowned restaurant</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 3 – Tequila Tasting & Team Time</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Team meetings with breakfast, coffee breaks and lunch</li>
                  <li>Afternoon tequila tasting on the beach</li>
                  <li>Sunset dinner with private beach setup</li>
                </ul>
              </div>
              <div>
                <h4 className="text-body-lg font-semibold text-landing-primary">Day 4 – Wrap-Up & Departure</h4>
                <ul className="text-body text-landing-secondary space-y-1 mt-2 pl-4">
                  <li>Final breakfast</li>
                  <li>Group shuttle departures to airport</li>
                </ul>
              </div>
            </div>
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h4 className="text-body-lg font-semibold text-landing-primary">Activities Included</h4>
              <ul className="text-body text-landing-secondary space-y-2 pl-4">
                <li>🍸 Mixology class with professional bartenders</li>
                <li>🛶 Guided group kayak tour</li>
                <li>🥃 Tequila tasting on the beach</li>
                <li>🍱 Family-style meals and curated dining</li>
              </ul>
            </div>
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-h4 text-landing-primary font-semibold">Who This Is For</h3>
              <p className="text-body text-landing-secondary">
                Perfect for teams looking to reconnect in a luxurious beachside setting. Ideal for executive retreats,
                strategic offsites, or incentive trips blending relaxation, collaboration, and authentic Mexican
                culture.
              </p>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <Button
                onClick={() => {
                  closeModal("cancun-details");
                  openModal("demo");
                }}
                className="w-full"
              >
                Book This Package
              </Button>
            </div>
          </div>
        </div>
      </UnifiedModal>
    </main>
  );
}
